/**
 * Pure browser-API client reset. Lives in @workspace/ui because the error-component
 * boundary needs it and @workspace/ui cannot depend on @workspace/shared. Apps that
 * also want telemetry use @workspace/shared/hooks#useResetClient instead, which calls
 * into the same browser APIs and adds a /diagnostics/reset beacon on top.
 *
 * Auth is preserved deliberately. Reset is for stale cache/state, not for forcing a
 * logout — if the user wanted that, they'd hit Logout.
 */

const PRESERVE_LOCAL_STORAGE = ["token", "theme"];
const PRESERVE_SESSION_STORAGE = ["impersonation_token"];

export async function resetClient(): Promise<void> {
	await Promise.allSettled([
		clearServiceWorkers(),
		clearCacheStorage(),
		clearIndexedDb(),
	]);
	clearWebStorage();
	if (typeof window !== "undefined") {
		window.location.replace(window.location.pathname + window.location.search);
	}
}

async function clearServiceWorkers(): Promise<void> {
	if (typeof navigator === "undefined" || !("serviceWorker" in navigator))
		return;
	try {
		const regs = await navigator.serviceWorker.getRegistrations();
		await Promise.allSettled(regs.map((r) => r.unregister()));
	} catch {
		/* best-effort */
	}
}

async function clearCacheStorage(): Promise<void> {
	if (typeof caches === "undefined") return;
	try {
		const keys = await caches.keys();
		await Promise.allSettled(keys.map((k) => caches.delete(k)));
	} catch {
		/* best-effort */
	}
}

async function clearIndexedDb(): Promise<void> {
	if (typeof indexedDB === "undefined") return;
	try {
		const databases = (
			indexedDB as IDBFactory & { databases?: () => Promise<IDBDatabaseInfo[]> }
		).databases;
		if (typeof databases !== "function") return;
		const dbs = await databases.call(indexedDB);
		await Promise.allSettled(
			dbs
				.map((d) => d.name)
				.filter((n): n is string => typeof n === "string" && n.length > 0)
				.map(
					(name) =>
						new Promise<void>((resolve) => {
							const req = indexedDB.deleteDatabase(name);
							req.onsuccess = () => resolve();
							req.onerror = () => resolve();
							req.onblocked = () => resolve();
						}),
				),
		);
	} catch {
		/* best-effort */
	}
}

function clearWebStorage(): void {
	if (typeof window === "undefined") return;
	preserveAndClear(window.localStorage, PRESERVE_LOCAL_STORAGE);
	preserveAndClear(window.sessionStorage, PRESERVE_SESSION_STORAGE);
}

function preserveAndClear(storage: Storage, preserveKeys: string[]): void {
	try {
		const preserved = new Map<string, string>();
		for (const key of preserveKeys) {
			const value = storage.getItem(key);
			if (value !== null) preserved.set(key, value);
		}
		storage.clear();
		for (const [key, value] of preserved) {
			storage.setItem(key, value);
		}
	} catch {
		/* privacy modes throw on access */
	}
}
